import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ResponsiveDialog from '../common/ResponsiveDialog';
import SearchPicker, { PickerOption } from './SearchPicker';
import CategoryPicker from './CategoryPicker';
import { readQrFromImage } from './qrFromImage';
import pettyCashApi from '../../services/pettyCashApi';
import type {
  AllocationType,
  ClaimAttachment,
  ClaimLineInput,
  LineCheck,
  LineType,
  PettyCashConfig,
  QrData,
  ReceiptAccount,
  VendorLookup,
  VendorMatch,
} from '../../types/pettyCash';
import {
  accountForCategory,
  categoryAccountText,
  categoryHasSide,
  checkVatFormat,
  cleanDateInput,
  emptyLine,
  formatAmount,
  formatDate,
  hasErrors,
  linePartFromQr,
  parseAmount,
  parseZatcaQr,
  rateOf,
  round2,
  todayIso,
  validateLine,
  vatOf,
} from '../../utils/pettyCash';

/**
 * One receipt of a petty cash claim. A VAT receipt names a SAP vendor found by its VAT number; a non-VAT
 * receipt only names the shop and posts to the company's cash supplier. A photo of the receipt is
 * attached, and its ZATCA QR code (when it can be read) fills the VAT number, date, total and VAT.
 * The fields carry the numbers the business uses for them (1 Supplier Name ... 11 VAT Tax Code).
 */

interface Props {
  open: boolean;
  companyId: number;
  config: PettyCashConfig;
  allocationType: AllocationType;
  /** The header's project or department code, and the project on the receipts of a department claim. */
  allocationCode: string;
  projectCode?: string | null;
  /** "3ESK1033 Air Products - ..." */
  allocationLabel: string;
  /** The line to edit; null adds a new one. */
  initial: ClaimLineInput | null;
  /** Number for a new line. */
  lineNum: number;
  /** Type of a new line. */
  defaultType: LineType;
  /** Files already attached to the line being edited. */
  attachments: ClaimAttachment[];
  onClose: () => void;
  /** Saves the line (the page saves the claim, then uploads the file). Rejects with the reason. */
  onSave: (line: ClaimLineInput, file: File | null) => Promise<void>;
  onOpenAttachment: (a: ClaimAttachment) => void;
  onDeleteAttachment?: (a: ClaimAttachment) => Promise<void>;
  /** "Define this supplier" when SAP has no vendor with the VAT number typed. */
  onDefineSupplier?: (vat: string, name: string) => void;
  /** A vendor defined meanwhile (Suppliers dialog): the dialog takes it. */
  definedVendor?: { cardCode: string; cardName: string; vat: string } | null;
}

type QrState = 'idle' | 'reading' | 'found' | 'none' | 'pdf';

/** What the server accepts as a receipt file (pettyCash.service ALLOWED_FILE_TYPES, MAX_FILE_BYTES). */
const FILE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif', 'application/pdf'];
const MAX_FILE_BYTES = 10 * 1024 * 1024;

/** The checks shown next to their field in each form; any other error is listed under the form. */
const FIELD_CODES: Record<LineType, ReadonlySet<string>> = {
  VAT: new Set(['supplier', 'vat-number', 'invoice-no', 'invoice-date', 'invoice-age', 'amount', 'vat-amount', 'total', 'category', 'auxiliary', 'vat-code', 'buyer-named']),
  NonVAT: new Set(['supplier', 'invoice-no', 'invoice-date', 'invoice-age', 'amount', 'category', 'auxiliary']),
};

const errorText = (e: any, fallback: string): string => e?.response?.data?.message || e?.message || fallback;
const amountText = (n: number): string => (n ? String(n) : '');

const ReceiptLineDialog: React.FC<Props> = ({
  open,
  companyId,
  config,
  allocationType,
  allocationCode,
  projectCode,
  allocationLabel,
  initial,
  lineNum,
  defaultType,
  attachments,
  onClose,
  onSave,
  onOpenAttachment,
  onDeleteAttachment,
  onDefineSupplier,
  definedVendor,
}) => {
  const { settings, vatCodes, categories, company } = config;
  const vatRegistered = company.vatRegistered;
  const [line, setLine] = useState<ClaimLineInput>(() => emptyLine(lineNum, defaultType, settings, vatCodes, vatRegistered));
  const [amountInput, setAmountInput] = useState('');
  const [vatInput, setVatInput] = useState('');
  const [vatTouched, setVatTouched] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [qrState, setQrState] = useState<QrState>('idle');
  const [lookup, setLookup] = useState<VendorLookup | null>(null);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [vendor, setVendor] = useState<VendorMatch | null>(null);
  const [showChecks, setShowChecks] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const lookupTicket = useRef(0);
  const searched = useRef(new Map<string, VendorMatch>());
  // The receipt's account for its category on this claim's side, and the auxiliaries (SAP Dimension 5) it takes.
  const [receipt, setReceipt] = useState<ReceiptAccount | null>(null);
  const [receiptBusy, setReceiptBusy] = useState(false);
  const [receiptError, setReceiptError] = useState<string | null>(null);
  const receiptTicket = useRef(0);

  // A fresh form each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    const start = initial ? { ...initial } : emptyLine(lineNum, vatRegistered ? defaultType : 'NonVAT', settings, vatCodes, vatRegistered);
    setLine(start);
    setAmountInput(amountText(start.amount));
    setVatInput(amountText(start.vatAmount));
    setVatTouched(!!initial);
    setFile(null);
    setFileError(null);
    setQrState('idle');
    setLookup(null);
    setLookupError(null);
    setVendor(start.cardCode ? {
      cardCode: start.cardCode, cardName: start.supplierName, vat: start.supplierVat || '', active: true, payTermsCode: null, cashTerms: false, vatValid: true,
    } : null);
    setShowChecks(false);
    setSaving(false);
    setSaveError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // The category's account for this side (SAP's rules in the claim's warehouse, else its fixed account) and the
  // auxiliaries that account takes, read again whenever the category changes.
  const categoryId = line.categoryId;
  useEffect(() => {
    const mine = ++receiptTicket.current;
    if (!open || !categoryId || !allocationCode) {
      setReceipt(null);
      setReceiptBusy(false);
      setReceiptError(null);
      return;
    }
    setReceiptBusy(true);
    setReceiptError(null);
    pettyCashApi.receiptAccount({ companyId, allocationType, allocationCode, projectCode, categoryId })
      .then((r) => { if (mine === receiptTicket.current) setReceipt(r); })
      .catch((e: any) => { if (mine === receiptTicket.current) { setReceipt(null); setReceiptError(errorText(e, 'The account of this category could not be read from SAP')); } })
      .finally(() => { if (mine === receiptTicket.current) setReceiptBusy(false); });
  }, [open, companyId, allocationType, allocationCode, projectCode, categoryId]);

  // The auxiliary follows the account: a choice the account still takes stays; otherwise the category's default for
  // this side, or none. An account that takes no auxiliary clears it.
  useEffect(() => {
    if (!receipt) return;
    const codes = receipt.auxiliaries?.codes || [];
    setLine((l) => {
      if (!receipt.auxiliaries) return l.auxiliary ? { ...l, auxiliary: null } : l;
      if (l.auxiliary && codes.some((c) => c.code === l.auxiliary)) return l;
      const preset = receipt.defaultAuxiliary && codes.some((c) => c.code === receipt.defaultAuxiliary) ? receipt.defaultAuxiliary : null;
      return (l.auxiliary || null) === preset ? l : { ...l, auxiliary: preset };
    });
  }, [receipt]);

  useEffect(() => {
    if (!file || !file.type.startsWith('image/')) { setPreviewUrl(null); return undefined; }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const vatLine = line.lineType === 'VAT';
  const category = categories.find((c) => c.categoryId === line.categoryId) || null;
  const accountHint = categoryAccountText(accountForCategory(category, allocationType));
  // Active categories with an account for this claim's side; the receipt's own category stays listed.
  const activeCategories = useMemo(
    () => categories
      .filter((c) => (c.isActive && categoryHasSide(c, allocationType)) || c.categoryId === line.categoryId)
      .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)),
    [categories, line.categoryId, allocationType],
  );

  /** Amount, VAT and total kept consistent: VAT follows amount x rate until the user types the VAT. */
  const recompute = useCallback((next: ClaimLineInput, touched: boolean): ClaimLineInput => {
    if (next.lineType !== 'VAT') return { ...next, vatAmount: 0, vatRate: 0, total: round2(next.amount) };
    const vat = touched ? round2(next.vatAmount) : vatOf(next.amount, next.vatRate);
    return { ...next, vatAmount: vat, total: round2(next.amount + vat) };
  }, []);

  const applyVendor = useCallback((v: VendorMatch | null) => {
    setVendor(v);
    setLine((l) => (v ? { ...l, cardCode: v.cardCode, supplierName: v.cardName, supplierVat: l.supplierVat || v.vat.replace(/\D/g, '') } : { ...l, cardCode: null }));
  }, []);

  const runLookup = useCallback(async (raw: string) => {
    const fmt = checkVatFormat(raw, company.vatCountry);
    if (!fmt.ok) return;
    const ticket = ++lookupTicket.current;
    setLookupBusy(true);
    setLookupError(null);
    try {
      const res = await pettyCashApi.lookupVendorByVat(companyId, fmt.digits);
      if (ticket !== lookupTicket.current) return;
      setLookup(res);
      const best = res.vendors.find((v) => v.active) || res.vendors[0] || null;
      applyVendor(best);
    } catch (e: any) {
      if (ticket === lookupTicket.current) setLookupError(errorText(e, 'SAP could not be asked for the vendor'));
    } finally {
      if (ticket === lookupTicket.current) setLookupBusy(false);
    }
  }, [applyVendor, company.vatCountry, companyId]);

  // A vendor defined meanwhile (Suppliers dialog) is taken at once.
  useEffect(() => {
    if (!open || !definedVendor) return;
    applyVendor({ cardCode: definedVendor.cardCode, cardName: definedVendor.cardName, vat: definedVendor.vat, active: true, payTermsCode: null, cashTerms: true, vatValid: true });
    setLine((l) => ({ ...l, supplierVat: definedVendor.vat.replace(/\D/g, '') || l.supplierVat }));
    setLookup(null);
  }, [applyVendor, definedVendor, open]);

  const setType = (type: LineType | null) => {
    if (!type || type === line.lineType) return;
    const code = type === 'VAT' ? settings.vatCode : settings.nonVatCode;
    const rate = type === 'VAT' ? rateOf(code, vatCodes) : 0;
    const paid = line.total || line.amount;
    const amount = type === 'VAT' ? round2(paid / (1 + rate / 100)) : paid;
    const next = recompute({
      ...line,
      lineType: type,
      vatCode: code || null,
      vatRate: rate,
      amount,
      vatAmount: type === 'VAT' ? round2(paid - amount) : 0,
      cardCode: type === 'VAT' ? line.cardCode : null,
    }, type === 'VAT');
    setLine(next);
    setAmountInput(amountText(next.amount));
    setVatInput(amountText(next.vatAmount));
    setVatTouched(type === 'VAT');
    if (type === 'NonVAT') setVendor(null);
  };

  const onFile = async (f: File | undefined) => {
    if (!f) return;
    // Refused here rather than after the receipt is saved: the server takes these types up to 10 MB.
    if (!FILE_TYPES.includes(String(f.type).toLowerCase())) { setFileError('Attach a photo (JPEG, PNG, WebP, HEIC) or a PDF.'); return; }
    if (f.size > MAX_FILE_BYTES) { setFileError(`${f.name} is larger than 10 MB: take a smaller photo or scan.`); return; }
    setFileError(null);
    setFile(f);
    if (!f.type.startsWith('image/')) { setQrState(f.type === 'application/pdf' ? 'pdf' : 'none'); return; }
    setQrState('reading');
    const qr: QrData | null = parseZatcaQr(await readQrFromImage(f));
    if (!qr) { setQrState('none'); return; }
    setQrState('found');
    const part = linePartFromQr(qr);
    const vatReceipt = line.lineType === 'VAT';
    setLine((l) => {
      const next: ClaimLineInput = { ...l, qr };
      if (part.invoiceDate) next.invoiceDate = part.invoiceDate;
      if (next.lineType === 'VAT') {
        if (part.supplierVat) next.supplierVat = part.supplierVat;
        if (part.amount !== undefined) next.amount = part.amount;
        if (part.vatAmount !== undefined) next.vatAmount = part.vatAmount;
        return recompute(next, true);
      }
      if (part.total !== undefined) next.amount = part.total;
      if (!next.supplierName && qr.seller) next.supplierName = qr.seller.slice(0, 200);
      return recompute(next, false);
    });
    if (vatReceipt) {
      if (part.amount !== undefined) setAmountInput(amountText(part.amount));
      if (part.vatAmount !== undefined) setVatInput(amountText(part.vatAmount));
      setVatTouched(true);
      if (part.supplierVat) void runLookup(part.supplierVat);
    } else if (part.total !== undefined) {
      setAmountInput(amountText(part.total));
    }
  };

  const ctx = {
    settings,
    vatRegistered,
    vatCountry: company.vatCountry,
    categories,
    vatCodes,
    today: todayIso(),
    hasImage: !!file || attachments.length > 0,
  };
  // What validateLine cannot see: the SAP vendor chosen and whether SAP's VAT codes could be read. The
  // server refuses to submit such a receipt (checkLine: vendor-inactive, vendor-vat).
  const vendorChecks: LineCheck[] = [];
  if (vatLine && vendor && line.cardCode && vendor.cardCode === line.cardCode) {
    if (!vendor.active) {
      vendorChecks.push({ level: 'error', code: 'vendor-inactive', message: `${vendor.cardName} (${vendor.cardCode}) is inactive in SAP: it cannot be used until SAP reactivates it, or record the receipt as a non-VAT receipt` });
    } else if (!vendor.vatValid) {
      vendorChecks.push({ level: 'error', code: 'vendor-vat', message: `SAP holds an invalid VAT number for ${vendor.cardName}: correct it in SAP first, or record the receipt as a non-VAT receipt` });
    }
  }
  if (vatLine && vatRegistered && !vatCodes.length) {
    vendorChecks.push({ level: 'error', code: 'vat-codes', message: 'The VAT codes could not be read from SAP: close the claim and open it again' });
  }
  // The auxiliary the receipt's account asks for (U_AUX_TYPE), required when SAP makes it mandatory (U_Aux_Mand).
  const auxList = receipt?.auxiliaries ?? null;
  const auxTypeName = auxList?.typeName || auxList?.type || receipt?.auxType || '';
  if (auxList && receipt?.auxMandatory && !line.auxiliary) {
    vendorChecks.push({ level: 'error', code: 'auxiliary', message: `Choose the auxiliary: ${receipt.account} needs a ${auxTypeName} auxiliary` });
  }
  const checks: LineCheck[] = [...validateLine(line, ctx), ...vendorChecks];
  const errors = checks.filter((c) => c.level === 'error');
  const otherErrors = errors.filter((c) => !FIELD_CODES[line.lineType].has(c.code));
  const warnings = checks.filter((c) => c.level === 'warning');
  const fieldError = (code: string) => (showChecks ? errors.find((c) => c.code === code)?.message : undefined);

  const save = async () => {
    setShowChecks(true);
    if (hasErrors(checks)) return;
    setSaving(true);
    setSaveError(null);
    try {
      await onSave({ ...line, supplierName: line.supplierName.trim(), invoiceNo: line.invoiceNo.trim(), description: line.description?.trim() || null }, file);
    } catch (e: any) {
      setSaveError(errorText(e, 'The receipt could not be saved'));
    } finally {
      setSaving(false);
    }
  };

  const vatFmt = checkVatFormat(line.supplierVat, company.vatCountry);
  const searchVendors = useCallback(async (q: string): Promise<PickerOption[]> => {
    const rows = await pettyCashApi.searchVendors(companyId, q);
    for (const v of rows) searched.current.set(v.cardCode, v);
    return rows.map((v) => ({ code: v.cardCode, name: v.cardName, hint: `VAT ${v.vat || 'none'}${v.active ? '' : ' · inactive'}` }));
  }, [companyId]);

  // The category's helper: the account this receipt gets for the claim's side, as the server works it out.
  const receiptHint = receiptBusy
    ? 'Reading the account…'
    : receiptError
      || (receipt
        ? receipt.account
          ? `Posts to ${receipt.account}${receipt.accountName ? ` ${receipt.accountName}` : ''}${receipt.source === 'rule' && receipt.rule ? ` (SAP rule ${receipt.rule.code})` : ''}`
          : `No account for this ${allocationType === 'Project' ? 'project' : 'department'} yet${receipt.note ? ` (${receipt.note})` : ''}: finance chooses one`
        : null);
  const categoryHelp = fieldError('category') || receiptHint || accountHint || (category ? 'This category has no account for this allocation yet' : undefined);

  // The auxiliary, shown only when the receipt's account takes one: the codes of the account's type, searchable.
  const auxOptions = auxList?.codes || [];
  const auxValue = line.auxiliary ? (auxOptions.find((c) => c.code === line.auxiliary) || { code: line.auxiliary, name: '', type: null }) : null;
  const auxField = (id: string) => (auxList ? (
    <Grid item xs={12} sm={6}>
      <Autocomplete
        id={id}
        options={auxOptions}
        value={auxValue}
        onChange={(_e, v) => setLine((l) => ({ ...l, auxiliary: v ? v.code : null }))}
        getOptionLabel={(o) => `${o.code}${o.name ? ` ${o.name}` : ''}`}
        isOptionEqualToValue={(a, b) => a.code === b.code}
        filterOptions={(opts, state) => {
          const words = state.inputValue.toLowerCase().split(/\s+/).filter(Boolean);
          return opts.filter((o) => words.every((w) => `${o.code} ${o.name}`.toLowerCase().includes(w))).slice(0, 200);
        }}
        autoHighlight
        noOptionsText="No auxiliary matches"
        renderInput={(params) => (
          <TextField
            {...params}
            label={`Auxiliary${auxTypeName ? ` (${auxTypeName})` : ''}`}
            required={!!receipt?.auxMandatory}
            error={!!fieldError('auxiliary')}
            helperText={fieldError('auxiliary') || `${receipt?.auxMandatory ? 'Required by' : 'Optional on'} ${receipt?.account || 'the account'}: its ${auxTypeName} codes`}
            placeholder="Type to search"
            InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
          />
        )}
      />
    </Grid>
  ) : null);

  const title = initial ? `Receipt ${initial.lineNum}` : vatLine ? 'Add VAT receipt' : 'Add non-VAT receipt';
  const n = (i: number) => `${i} · `;

  return (
    <ResponsiveDialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth aria-labelledby="receipt-dialog-title">
      <DialogTitle id="receipt-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
        <Box component="span" sx={{ flexGrow: 1 }}>{title}</Box>
        <IconButton aria-label="Close" onClick={onClose} disabled={saving}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {vatRegistered ? (
            <ToggleButtonGroup
              exclusive
              size="small"
              value={line.lineType}
              onChange={(_e, v) => setType(v)}
              aria-label="Receipt type"
              sx={{ alignSelf: 'flex-start' }}
            >
              <ToggleButton value="VAT">VAT supplier</ToggleButton>
              <ToggleButton value="NonVAT">Non-VAT supplier</ToggleButton>
            </ToggleButtonGroup>
          ) : null}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
            <Box sx={{ width: 140, height: 186, flex: '0 0 auto', border: '1px dashed #9aa6ad', borderRadius: 1, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fafbfb' }}>
              {previewUrl ? (
                <img src={previewUrl} alt="The receipt chosen" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }} />
              ) : (
                <Typography variant="caption" color="text.secondary" sx={{ p: 1, textAlign: 'center' }}>
                  {file ? file.name : attachments.length ? `${attachments.length} file${attachments.length === 1 ? '' : 's'} attached` : 'No receipt yet'}
                </Typography>
              )}
            </Box>
            <Stack spacing={1} sx={{ flexGrow: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button component="label" variant="outlined" startIcon={<PhotoCameraIcon />} disabled={saving}>
                  {file ? 'Choose another photo or PDF' : 'Photo or PDF of the receipt'}
                  <input hidden type="file" accept="image/*,application/pdf" onChange={(e) => { void onFile(e.target.files?.[0]); e.target.value = ''; }} />
                </Button>
              </Stack>
              {qrState === 'reading' ? <Typography variant="body2" color="text.secondary"><CircularProgress size={14} sx={{ mr: 1 }} />Reading the QR code…</Typography> : null}
              {qrState === 'found' && line.qr ? (
                <Alert severity="success" sx={{ py: 0 }}>
                  Read from the receipt's QR code: {[
                    line.qr.vatNumber ? `VAT no. ${line.qr.vatNumber}` : '',
                    line.qr.timestamp ? `date ${formatDate(line.qr.timestamp) || line.qr.timestamp}` : '',
                    line.qr.totalWithVat ? `total ${line.qr.totalWithVat}` : '',
                    line.qr.vatTotal ? `VAT ${line.qr.vatTotal}` : '',
                  ].filter(Boolean).join(' · ')}. The invoice number is not in the QR code: type it.
                </Alert>
              ) : null}
              {qrState === 'none' ? <Typography variant="body2" color="text.secondary">No QR code could be read on this photo. Type the fields below.</Typography> : null}
              {qrState === 'pdf' ? <Typography variant="body2" color="text.secondary">QR codes are read from photos only. Type the fields below.</Typography> : null}
              {fileError ? <Alert severity="error" sx={{ py: 0 }}>{fileError}</Alert> : null}
              {attachments.length ? (
                <Stack spacing={0.5}>
                  {attachments.map((a) => (
                    <Stack key={a.attachmentId} direction="row" spacing={1} alignItems="center">
                      <Link component="button" type="button" variant="body2" onClick={() => onOpenAttachment(a)}>{a.fileName}</Link>
                      {a.duplicates?.length ? <Typography variant="caption" color="warning.main">Same file as {a.duplicates.map((d) => `${d.claimNo || 'a draft'}${d.lineNum ? ` line ${d.lineNum}` : ''}`).join(', ')}</Typography> : null}
                      {onDeleteAttachment ? (
                        <IconButton size="small" aria-label={`Remove ${a.fileName}`} onClick={() => { void onDeleteAttachment(a); }} disabled={saving}><DeleteOutlineIcon fontSize="small" /></IconButton>
                      ) : null}
                    </Stack>
                  ))}
                </Stack>
              ) : null}
            </Stack>
          </Stack>

          {vatLine ? (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <SearchPicker
                  id="pc-supplier"
                  label={`${n(1)}Supplier Name`}
                  value={line.cardCode ? { code: line.cardCode, name: line.supplierName } : null}
                  onChange={(o) => {
                    const match = o ? searched.current.get(o.code) || null : null;
                    applyVendor(match);
                    if (match) setLine((l) => ({ ...l, supplierVat: match.vat.replace(/\D/g, '') || l.supplierVat }));
                    setLookup(null);
                  }}
                  search={searchVendors}
                  helperText={fieldError('supplier') || 'From SAP: search by name, or type the VAT number'}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="pc-vat"
                  label={`${n(2)}Supplier VAT Registration Number`}
                  value={line.supplierVat || ''}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^\d\s-]/g, '').slice(0, 20);
                    setLine((l) => ({ ...l, supplierVat: v, cardCode: null }));
                    setVendor(null);
                    setLookup(null);
                    if (checkVatFormat(v, company.vatCountry).ok) void runLookup(v);
                  }}
                  onBlur={() => { if (line.supplierVat && !line.cardCode) void runLookup(line.supplierVat); }}
                  inputProps={{ inputMode: 'numeric' }}
                  error={!!fieldError('vat-number')}
                  helperText={fieldError('vat-number') || (line.supplierVat && !vatFmt.ok ? vatFmt.message : 'PRPO finds the supplier in SAP with it')}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                {lookupBusy ? <Typography variant="body2" color="text.secondary"><CircularProgress size={14} sx={{ mr: 1 }} />Looking for the vendor in SAP…</Typography> : null}
                {lookupError ? <Alert severity="error">{lookupError}</Alert> : null}
                {vendor && line.cardCode ? (
                  <Alert severity={vendor.active ? 'info' : 'error'} sx={{ py: 0 }}>
                    <strong>{vendor.active ? 'Found in SAP' : 'Inactive in SAP'}: {vendor.cardCode} {vendor.cardName}</strong>
                    {!vendor.active ? ' — this vendor is inactive or frozen; it cannot be used until SAP reactivates it.' : ''}
                    {vendor.active && !vendor.vatValid ? ' — SAP holds its VAT number in a wrong format; ask for it to be corrected.' : ''}
                    {vendor.active && lookup && !vendor.cashTerms ? ' — not on the "immediately" payment terms: make sure this receipt was paid in cash.' : ''}
                  </Alert>
                ) : null}
                {lookup && lookup.vendors.length > 1 ? (
                  <TextField
                    id="pc-vendor-choice"
                    select
                    label="Several SAP vendors have this VAT number"
                    value={line.cardCode || ''}
                    onChange={(e) => applyVendor(lookup.vendors.find((v) => v.cardCode === e.target.value) || null)}
                    fullWidth
                    sx={{ mt: 1 }}
                  >
                    {lookup.vendors.map((v) => <MenuItem key={v.cardCode} value={v.cardCode}>{v.cardCode} {v.cardName}{v.active ? '' : ' (inactive)'}</MenuItem>)}
                  </TextField>
                ) : null}
                {lookup && lookup.vendors.length === 0 ? (
                  <Alert
                    severity="warning"
                    action={onDefineSupplier ? <Button color="inherit" size="small" onClick={() => onDefineSupplier(lookup.digits, line.supplierName || line.qr?.seller || '')}>Define this supplier</Button> : undefined}
                  >
                    No vendor in SAP has VAT number {lookup.digits}. Define the supplier, or record the receipt as a non-VAT receipt.
                  </Alert>
                ) : null}
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField id="pc-taxinv" label={`${n(3)}Tax Invoice Number`} value={line.invoiceNo} onChange={(e) => setLine((l) => ({ ...l, invoiceNo: e.target.value.slice(0, 100) }))} error={!!fieldError('invoice-no')} helperText={fieldError('invoice-no')} required fullWidth />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField id="pc-taxdate" type="date" label={`${n(4)}Tax Invoice Date`} value={line.invoiceDate} onChange={(e) => { const d = cleanDateInput(e.target.value); setLine((l) => ({ ...l, invoiceDate: d })); }} InputLabelProps={{ shrink: true }} inputProps={{ max: todayIso() }} error={!!(fieldError('invoice-date') || fieldError('invoice-age'))} helperText={fieldError('invoice-date') || fieldError('invoice-age')} required fullWidth />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="pc-amount"
                  label={`${n(5)}Invoice Amount Before VAT`}
                  value={amountInput}
                  onChange={(e) => {
                    setAmountInput(e.target.value);
                    const next = recompute({ ...line, amount: parseAmount(e.target.value) ?? 0 }, vatTouched);
                    setLine(next);
                    if (!vatTouched) setVatInput(amountText(next.vatAmount));
                  }}
                  inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' } }}
                  error={!!fieldError('amount')}
                  helperText={fieldError('amount')}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField id="pc-rate" label={`${n(6)}VAT Rate (%)`} value={String(line.vatRate)} InputProps={{ readOnly: true }} inputProps={{ style: { textAlign: 'right' } }} fullWidth />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  id="pc-vatamount"
                  label={`${n(7)}VAT Amount`}
                  value={vatInput}
                  onChange={(e) => {
                    setVatInput(e.target.value);
                    setVatTouched(true);
                    const v = parseAmount(e.target.value) ?? 0;
                    setLine((l) => recompute({ ...l, vatAmount: v }, true));
                  }}
                  inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' } }}
                  error={!!fieldError('vat-amount')}
                  helperText={fieldError('vat-amount') || (line.amount > 0 ? `${formatAmount(line.amount)} x ${line.vatRate}% = ${formatAmount(vatOf(line.amount, line.vatRate))}` : undefined)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField id="pc-total" label={`${n(8)}Total Invoice Amount Including VAT`} value={formatAmount(line.total)} InputProps={{ readOnly: true }} inputProps={{ style: { textAlign: 'right', fontWeight: 600 } }} error={!!fieldError('total')} helperText={fieldError('total')} fullWidth />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField id="pc-alloc" label={`${n(9)}Project / Cost Center`} value={`${allocationLabel} (from the header)`} InputProps={{ readOnly: true }} fullWidth />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CategoryPicker
                  id="pc-category"
                  label={`${n(10)}Expense Category`}
                  categories={activeCategories}
                  value={line.categoryId ?? null}
                  allocationType={allocationType}
                  onChange={(categoryId) => setLine((l) => ({ ...l, categoryId }))}
                  error={!!fieldError('category')}
                  helperText={categoryHelp}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="pc-vatcode"
                  select
                  label={`${n(11)}VAT Tax Code`}
                  value={line.vatCode || ''}
                  onChange={(e) => {
                    const code = e.target.value;
                    const next = recompute({ ...line, vatCode: code, vatRate: rateOf(code, vatCodes) }, false);
                    setLine(next);
                    setVatInput(amountText(next.vatAmount));
                    setVatTouched(false);
                  }}
                  error={!!fieldError('vat-code')}
                  helperText={fieldError('vat-code')}
                  required
                  fullWidth
                >
                  {vatCodes.filter((c) => c.code !== settings.nonVatCode).map((c) => <MenuItem key={c.code} value={c.code}>{c.code} · {c.name} ({c.rate}%)</MenuItem>)}
                </TextField>
              </Grid>
              {auxField('pc-aux')}
              <Grid item xs={12}>
                <TextField id="pc-desc" label="Description" value={line.description || ''} onChange={(e) => setLine((l) => ({ ...l, description: e.target.value.slice(0, 254) }))} placeholder="What was bought" fullWidth />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Checkbox checked={line.buyerNamed} onChange={(e) => setLine((l) => ({ ...l, buyerNamed: e.target.checked }))} />}
                  label={`The invoice names ${company.name} and its VAT number${settings.requireBuyerNamed ? ' (required for a VAT receipt)' : ''}`}
                />
                {fieldError('buyer-named') ? <Typography variant="caption" color="error" display="block">{fieldError('buyer-named')}</Typography> : null}
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField id="pc-nv-supplier" label={`${n(1)}Supplier Name`} value={line.supplierName} onChange={(e) => setLine((l) => ({ ...l, supplierName: e.target.value.slice(0, 200) }))} placeholder="As on the receipt" error={!!fieldError('supplier')} helperText={fieldError('supplier') || `Posts to ${settings.cashSupplier ? `${settings.cashSupplier} ${settings.cashSupplierName || ''}`.trim() : 'the company\'s cash supplier'}`} required fullWidth />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField id="pc-nv-inv" label={`${n(2)}Invoice Number`} value={line.invoiceNo} onChange={(e) => setLine((l) => ({ ...l, invoiceNo: e.target.value.slice(0, 100) }))} error={!!fieldError('invoice-no')} helperText={fieldError('invoice-no')} required fullWidth />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField id="pc-nv-date" type="date" label={`${n(3)}Invoice Date`} value={line.invoiceDate} onChange={(e) => { const d = cleanDateInput(e.target.value); setLine((l) => ({ ...l, invoiceDate: d })); }} InputLabelProps={{ shrink: true }} inputProps={{ max: todayIso() }} error={!!(fieldError('invoice-date') || fieldError('invoice-age'))} helperText={fieldError('invoice-date') || fieldError('invoice-age')} required fullWidth />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="pc-nv-amount"
                  label={`${n(4)}Invoice Amount`}
                  value={amountInput}
                  onChange={(e) => {
                    setAmountInput(e.target.value);
                    const a = parseAmount(e.target.value) ?? 0;
                    setLine((l) => recompute({ ...l, amount: a }, false));
                  }}
                  inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' } }}
                  error={!!fieldError('amount')}
                  helperText={fieldError('amount')}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField id="pc-nv-total" label={`${n(5)}Total Invoice Amount`} value={formatAmount(line.total)} InputProps={{ readOnly: true }} inputProps={{ style: { textAlign: 'right', fontWeight: 600 } }} fullWidth />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField id="pc-nv-alloc" label={`${n(6)}Project / Cost Center`} value={`${allocationLabel} (from the header)`} InputProps={{ readOnly: true }} fullWidth />
              </Grid>
              <Grid item xs={12} sm={vatRegistered ? 6 : 12}>
                <CategoryPicker
                  id="pc-nv-category"
                  label={`${n(7)}Expense Category`}
                  categories={activeCategories}
                  value={line.categoryId ?? null}
                  allocationType={allocationType}
                  onChange={(categoryId) => setLine((l) => ({ ...l, categoryId }))}
                  error={!!fieldError('category')}
                  helperText={categoryHelp}
                  required
                />
              </Grid>
              {vatRegistered ? (
                <Grid item xs={12} sm={6}>
                  <TextField id="pc-nv-vatcode" label={`${n(8)}VAT Tax Code`} value={settings.nonVatCode || ''} InputProps={{ readOnly: true }} helperText="Fixed for non-VAT receipts" fullWidth />
                </Grid>
              ) : null}
              {auxField('pc-nv-aux')}
              <Grid item xs={12}>
                <TextField id="pc-nv-desc" label="Description" value={line.description || ''} onChange={(e) => setLine((l) => ({ ...l, description: e.target.value.slice(0, 254) }))} placeholder="What was bought" fullWidth />
              </Grid>
            </Grid>
          )}

          {showChecks ? otherErrors.map((c, i) => <Alert key={`${c.code}-${i}`} severity="error">{c.message}</Alert>) : null}
          {warnings.map((w, i) => <Alert key={`${w.code}-${i}`} severity="warning">{w.message}</Alert>)}
          {saveError ? <Alert severity="error">{saveError}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        {showChecks && errors.length ? <Typography variant="body2" color="error" sx={{ mr: 'auto', pl: 1 }}>{errors.length} field{errors.length === 1 ? '' : 's'} to complete</Typography> : null}
        <Button variant="outlined" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={() => { void save(); }} disabled={saving}>{saving ? 'Saving…' : initial ? 'Save receipt' : 'Add receipt'}</Button>
      </DialogActions>
    </ResponsiveDialog>
  );
};

export default ReceiptLineDialog;
